import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '../api/client';
import { DataTable, EmptyState } from '../components/common';
import { useTimeframes } from '../utils/timeframes';
import { TODO_STATUS } from '../constants';

/**
 * Dashboard - Shows task overview and meetings
 */
const Dashboard = ({ user, setView, setSelectedMeeting }) => {
  const [meetings, setMeetings] = useState([]);
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);

  const timeframes = useTimeframes();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [meetingsData, todosData] = await Promise.all([
        api.get('/meetings'),
        api.get('/todos/my')
      ]);
      setMeetings(meetingsData);
      setTodos(todosData.filter(t => t.status !== TODO_STATUS.DONE));
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Categorize tasks by due date
  const taskCategories = useMemo(() => {
    const { startOfToday, endOfWeek, endOfNextWeek, endOfNextMonth } = timeframes;
    
    const categories = {
      overdue: 0,
      dueThisWeek: 0,
      dueNextWeek: 0,
      dueNextMonth: 0,
      dueLater: 0
    };

    todos.forEach(todo => {
      if (!todo.dueDate || todo.status === TODO_STATUS.DONE) return;
      
      const dueDate = new Date(todo.dueDate);
      
      if (dueDate < startOfToday) {
        categories.overdue++;
      } else if (dueDate <= endOfWeek) {
        categories.dueThisWeek++;
      } else if (dueDate <= endOfNextWeek) {
        categories.dueNextWeek++;
      } else if (dueDate <= endOfNextMonth) {
        categories.dueNextMonth++;
      } else {
        categories.dueLater++;
      }
    });

    return categories;
  }, [todos, timeframes]);

  const meetingColumns = [
    { key: 'name', label: 'Meeting Name' },
    { 
      key: 'participants', 
      label: 'Participants',
      render: (participants) => `${participants.length} participants`
    },
  ];

  const handleMeetingClick = (meeting) => {
    setSelectedMeeting(meeting);
    setView('meeting-detail');
  };

  if (loading) {
    return (
      <div className="page-header">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
      </div>

      {/* Task Status Tiles */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.1rem', marginBottom: '0.75rem', color: 'var(--color-text)' }}>
          My Task Overview
        </h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(5, 1fr)', 
          gap: '1rem' 
        }}>
          <div
            style={{
              background: '#F09595',
              padding: '1.25rem',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'box-shadow 0.15s ease',
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.25)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            onClick={() => setView('my-todos', 'overdue', 'active')}
          >
            <div style={{ 
              fontSize: '2rem', 
              fontWeight: '700', 
              color: '#501313', 
              marginBottom: '0.25rem' 
            }}>
              {taskCategories.overdue}
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#791F1F' }}>
              Overdue
            </div>
          </div>

          <div
            style={{
              background: '#F0997B',
              padding: '1.25rem',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'box-shadow 0.15s ease',
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.25)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            onClick={() => setView('my-todos', 'thisWeek', 'active')}
          >
            <div style={{ 
              fontSize: '2rem', 
              fontWeight: '700', 
              color: '#4A1B0C', 
              marginBottom: '0.25rem' 
            }}>
              {taskCategories.dueThisWeek}
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#712B13' }}>
              Due This Week
            </div>
          </div>

          <div
            style={{
              background: '#85B7EB',
              padding: '1.25rem',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'box-shadow 0.15s ease',
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.25)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            onClick={() => setView('my-todos', 'nextWeek', 'active')}
          >
            <div style={{ 
              fontSize: '2rem', 
              fontWeight: '700', 
              color: '#042C53', 
              marginBottom: '0.25rem' 
            }}>
              {taskCategories.dueNextWeek}
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#0C447C' }}>
              Due Next Week
            </div>
          </div>

          <div
            style={{
              background: '#5DCAA5',
              padding: '1.25rem',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'box-shadow 0.15s ease',
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.25)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            onClick={() => setView('my-todos', 'nextMonth', 'active')}
          >
            <div style={{ 
              fontSize: '2rem', 
              fontWeight: '700', 
              color: '#04342C', 
              marginBottom: '0.25rem' 
            }}>
              {taskCategories.dueNextMonth}
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#085041' }}>
              Due in next 30 Days
            </div>
          </div>

          <div
            style={{
              background: '#B4B2A9',
              padding: '1.25rem',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'box-shadow 0.15s ease',
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.25)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            onClick={() => setView('my-todos', 'later', 'active')}
          >
            <div style={{ 
              fontSize: '2rem', 
              fontWeight: '700', 
              color: '#2C2C2A', 
              marginBottom: '0.25rem' 
            }}>
              {taskCategories.dueLater}
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#444441' }}>
              Due Later
            </div>
          </div>
        </div>
      </div>

      {/* My Meetings */}
      <div className="card">
        <h2 className="card-title">My Meetings ({meetings.length})</h2>
        {meetings.length === 0 ? (
          <EmptyState
            icon="📅"
            title="No meetings yet"
            message="You haven't been added to any meetings."
          />
        ) : (
          <DataTable
            columns={meetingColumns}
            data={meetings}
            onRowClick={handleMeetingClick}
          />
        )}
      </div>
    </div>
  );
};

Dashboard.propTypes = {
  user: PropTypes.object.isRequired,
  setView: PropTypes.func.isRequired,
  setSelectedMeeting: PropTypes.func.isRequired,
};

export default Dashboard;
